import React from 'react';
import { theme, fmtNum } from './theme';

type Props = {
  title: string;
  subtitle?: string;
  value: number | string;
  unit?: string;             // e.g. "Pallets" / "Locations"
  icon?: React.ReactNode;
  loading?: boolean;
  accent?: string;
  onClick?: () => void;
  active?: boolean;
};

const KpiCard: React.FC<Props> = ({
  title, subtitle, value, unit, loading,
  accent = theme.palette?.neutral ?? theme.accents.blue, onClick, active,
}) => {
  // The whole card is a real <button> so it has native click semantics +
  // keyboard accessibility (Enter / Space). No "click to drill" hint needed.
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        minWidth: 0,
        textAlign: 'left',
        fontFamily: theme.fontFamily,
        position: 'relative',
        background: '#ffffff',
        border: active ? `1px solid ${accent}` : '1px solid #e5eaf2',
        borderRadius: 12,
        padding: '20px 22px 16px',
        boxShadow: active
          ? `${theme.cardShadowHover}, 0 0 0 3px ${accent}22`
          : theme.cardShadow,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease',
        overflow: 'hidden',
        font: 'inherit',
      }}
      onMouseEnter={e => {
        if (onClick) {
          (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-3px)';
          (e.currentTarget as HTMLButtonElement).style.boxShadow = theme.cardShadowHover;
        }
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
        (e.currentTarget as HTMLButtonElement).style.boxShadow = active
          ? `${theme.cardShadowHover}, 0 0 0 3px ${accent}22`
          : theme.cardShadow;
      }}
    >
      {/* left accent rail */}
      <div style={{
        position: 'absolute', top: 0, bottom: 0, left: 0, width: 3,
        background: accent,
        borderTopLeftRadius: 12, borderBottomLeftRadius: 12,
      }} />

      {/* header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 12, fontWeight: 600, letterSpacing: 0.2,
            color: theme.textSecondary,
          }}>
            {title}
          </div>
          {subtitle && (
            <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>
              {subtitle}
            </div>
          )}
        </div>
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: accent, marginTop: 6, flexShrink: 0,
          boxShadow: `0 0 0 3px ${accent}22`,
        }} />
      </div>

      {/* big value — exact full count, no abbreviation */}
      {loading ? (
        <div style={{
          marginTop: 16, height: 30, width: '70%', borderRadius: 7,
          background: 'linear-gradient(90deg, #eef2f8 0%, #f7faff 50%, #eef2f8 100%)',
          backgroundSize: '200% 100%',
          animation: 'kpi-shimmer 1.4s ease-in-out infinite',
        }} />
      ) : (
        <div style={{
          marginTop: 14, lineHeight: 1.05,
          display: 'flex', alignItems: 'baseline', gap: 6,
        }}>
          <span style={{
            fontSize: 30, fontWeight: 700, color: theme.textPrimary,
            letterSpacing: -0.6, fontVariantNumeric: 'tabular-nums',
          }}>
            {fmtNum(value)}
          </span>
          {unit && (
            <span style={{ fontSize: 12, fontWeight: 600, color: theme.textMuted }}>
              {unit}
            </span>
          )}
        </div>
      )}

    </button>
  );
};

export default KpiCard;
